import { useMemo } from "react";
import type { FixedCost } from "../types.ts";
import { formatAmount } from "../format.ts";

type Props = {
  fixedCosts: FixedCost[];
  allFixedCosts: FixedCost[];
  filterLabel: string | null;
};

function sumTotals(fixedCosts: FixedCost[]) {
  let monthly = 0;
  let quarterly = 0;
  let yearly = 0;
  for (const c of fixedCosts) {
    const n = Number(c.amount) || 0;
    if (c.occurrence === "monthly") monthly += n;
    else if (c.occurrence === "quarterly") quarterly += n;
    else if (c.occurrence === "yearly") yearly += n;
  }
  const perMonth = monthly + quarterly / 3 + yearly / 12;
  return { monthly, quarterly, yearly, perMonth };
}

export function FixedCostsSummary({
  fixedCosts,
  allFixedCosts,
  filterLabel,
}: Props) {
  const totals = useMemo(() => sumTotals(fixedCosts), [fixedCosts]);
  const overallPerMonth = useMemo(
    () => (filterLabel ? sumTotals(allFixedCosts).perMonth : null),
    [allFixedCosts, filterLabel],
  );

  return (
    <div className="fixed-costs-summary">
      <Stat label="Månad" value={totals.monthly} />
      <Stat label="Kvartal" value={totals.quarterly} />
      <Stat label="År" value={totals.yearly} />
      <Stat
        label={filterLabel ? `${filterLabel} / mån` : "Totalt / mån"}
        value={totals.perMonth}
        note={
          overallPerMonth === null
            ? undefined
            : `av ${formatAmount(overallPerMonth)} totalt`
        }
        emphasis
      />
    </div>
  );
}

function Stat({
  label,
  value,
  note,
  emphasis,
}: {
  label: string;
  value: number;
  note?: string;
  emphasis?: boolean;
}) {
  return (
    <div className={`stat${emphasis ? " stat-emphasis" : ""}`}>
      <span className="stat-label">{label}</span>
      <b className={value < 0 ? "neg" : ""}>{formatAmount(value)}</b>
      {note && <span className="stat-note">{note}</span>}
    </div>
  );
}
